import { Link, useNavigate } from "react-router-dom";
import styled from "styled-components";
import {
    getMetaData,
    loadAccountDetail,
    selectAccount,
    useAppDispatch,
    useAppSelector,
} from "@redux";
import { IconLogOut, SwitchCommon } from "@components";
import { IItemMenuHeader } from "@interfaces";
import { breakpoint, useLogoutHandler } from "@utilities";
import { useEffect, useState } from "react";
import { pointsApi } from "@api";
import { PATH_COMMISSION_AGENT, PATH_POINTS_HISTORY, PATH_UPDATE_PASSWORD } from "@routes";
import { shouldShowUpgradeToCommission } from "./menuVisibility";

interface IProps {
    listMenuItems: IItemMenuHeader[];
}

export const MenuHeaderComponent = (props: IProps) => {
    const { listMenuItems } = props;
    const dispatch = useAppDispatch();
    const { account, metadata } = useAppSelector(selectAccount);
    const navigate = useNavigate();
    useEffect(() => {
        dispatch(getMetaData());
    }, []);
    const handleLogout = useLogoutHandler();

    const isPointsDisplayed = Boolean(account?.is_points_displayed);
    const [checked, setChecked] = useState<boolean>(isPointsDisplayed);
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        setChecked(isPointsDisplayed);
    }, [isPointsDisplayed]);

    const handleToggle = async (nextChecked: boolean) => {
        if (loading) return;
        const previous = checked;
        setChecked(nextChecked);
        setLoading(true);
        try {
            const res = await pointsApi.showHidePoints();
            if (res.data.code === 200) {
                await dispatch(loadAccountDetail());
            }
        } catch (err) {
            setChecked(previous);
            console.error("Failed to toggle points visibility:", err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container>
            {
                <>
                    {account?.is_agent_integrated &&
                        (metadata?.is_commission_agent || metadata?.is_points_agent) && (
                            <div
                                className="linkItem showPoints"
                                onClick={() => navigate(PATH_POINTS_HISTORY)}
                            >
                                <div className="itemWrapper">
                                    <div className="point">
                                        <span className="icon">
                                            <img src="/images/Reward1.svg" alt="gift" />
                                        </span>
                                        <span className="linkText">
                                            {metadata?.is_commission_agent
                                                ? "Show Commissions"
                                                : "Show Points"}
                                        </span>
                                    </div>
                                    <div
                                        onMouseDown={(e) => e.stopPropagation()}
                                        onClick={(e) => e.stopPropagation()}
                                        onTouchStart={(e) => e.stopPropagation()}
                                        onFocusCapture={(e) => e.stopPropagation()}
                                    >
                                        <SwitchCommon
                                            isChecked={checked}
                                            setIsChecked={(v) => {
                                                if (loading) return;
                                                handleToggle(v);
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    {listMenuItems.map((menuItem, index) => {
                        const Icon = menuItem.icon;
                        // Keyed on the route, not the label: the password entry
                        // reads "Set Password" or "Update Password" depending on
                        // whether one has ever been chosen.
                        if (
                            account?.sso_type !== "email" &&
                            menuItem.link === PATH_UPDATE_PASSWORD
                        ) {
                            return null;
                        }
                        const body = (
                            <div className="itemWrapper">
                                <span className="icon">
                                    {/* <IconHeart /> */}
                                    <Icon />
                                </span>
                                <span className="linkText">{menuItem.title}</span>
                                {menuItem.alert && (
                                    <span className="itemAlert" aria-hidden="true">
                                        !
                                    </span>
                                )}
                            </div>
                        );
                        return menuItem.onClick ? (
                            <div className="linkItem" onClick={menuItem.onClick} key={index}>
                                {body}
                            </div>
                        ) : (
                            <Link className="linkItem" to={menuItem.link} key={index}>
                                {body}
                            </Link>
                        );
                    })}
                    {shouldShowUpgradeToCommission() &&
                        account?.is_agent_integrated &&
                        !metadata?.is_commission_agent &&
                        metadata?.is_upgrade_to_commission_enabled && (
                            <div
                                className="linkItem upgradeCommission"
                                onClick={() => navigate(PATH_COMMISSION_AGENT)}
                            >
                                <div className="itemWrapper">
                                    <span className="icon">
                                        <img src="/images/Reward1.svg" alt="gift" />
                                    </span>
                                    <span className="linkText">Upgrade to Commission agent</span>
                                </div>
                            </div>
                        )}
                    <div className="linkItem logOut" onClick={handleLogout}>
                        <div className="itemWrapper">
                            <span className="icon">
                                <IconLogOut />
                            </span>
                            <span className="linkText">Logout</span>
                        </div>
                    </div>
                </>
            }
        </Container>
    );
};

const Container = styled.div`
    width: 325px;
    background-color: #fff;
    position: absolute;
    ${breakpoint.breakOnlyMobile`
        top: calc(100% - 20px);
    `}
    top: calc(100% + 20px);
    right: 0;
    box-shadow: 4px 4px 45px rgba(0, 0, 0, 0.08);
    border: 1px solid ${(p) => p.theme.colors.gray_5};
    padding: 0 30px 0 16px;

    .linkItem {
        cursor: pointer;

        .itemWrapper {
            display: flex;
            align-items: center;
            padding: 24px 0;
            border-bottom: 1px solid ${(p) => p.theme.colors.gray_5};
        }

        .linkText {
            font-size: 16px;
            font-weight: 500;
            line-height: 21px;
            color: #000;
        }

        /* The same mark the account icon carries, sat inline beside the label
           rather than overlapping it. */
        .itemAlert {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 15px;
            height: 15px;
            margin-left: 8px;
            border-radius: 50%;
            background-color: ${(p) => p.theme.colors.red};
            color: #fff;
            font-size: 10px;
            font-weight: 700;
            line-height: 1;
            flex-shrink: 0;
        }

        .icon {
            margin-right: 16px;

            svg {
                width: 20px;

                path {
                    stroke: ${(p) => p.theme.colors.gray_1} !important;
                }
            }
        }

        &:last-child {
            .itemWrapper {
                border-bottom: none;
            }
        }
    }

    .logOut {
        color: ${(p) => p.theme.colors.gray_3};

        .itemWrapper {
            .icon svg {
                path {
                    stroke: ${(p) => p.theme.colors.red} !important;
                }
            }

            .linkText {
                color: ${(p) => p.theme.colors.red};
            }
        }
    }

    .showPoints {
        cursor: pointer;

        .itemWrapper {
            display: flex;
            align-items: center;
            justify-content: space-between;

            .point {
                display: flex;
                align-items: center;
            }
        }
        .linkText {
            color: ${(p) => p.theme.colors.black};
        }
    }

    .upgradeCommission {
        .linkText {
            color: ${(p) => p.theme.colors.black};
        }
    }
`;
